import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Testimonials from '../home/components/Testimonials';
import ServiceAreas from '../home/components/ServiceAreas';
import { projects } from '../../mocks/projects';
import { homeFaqs } from '../../mocks/faqs';

const extraImages = [
  { id: 18, title: 'Tampa Patio Design', category: 'Patios', large: 'https://public.readdy.ai/ai/img_res/1d287f140b0becc134a0a6df118dafd1.jpg', material: 'Belgard Appian Stone' },
  { id: 19, title: 'Pool Deck Pavers', category: 'Pool Decks', large: 'https://readdy.ai/api/search-image?query=luxury%20travertine%20style%20pool%20deck%20pavers%20surrounding%20a%20sparkling%20blue%20swimming%20pool%20in%20Florida%20backyard%2C%20elegant%20light%20stone%20color%2C%20palm%20trees%2C%20premium%20hardscape%20design%2C%20professional%20real%20estate%20photography&width=800&height=600&seq=gal2&orientation=landscape', material: 'Tremron Travertine' },
  { id: 20, title: 'Retaining Wall & Steps', category: 'Walls', large: 'https://readdy.ai/api/search-image?query=beautiful%20natural%20stone%20retaining%20wall%20with%20steps%20and%20integrated%20landscaping%20in%20a%20residential%20front%20yard%2C%20Tampa%20Florida%20style%2C%20warm%20earth%20tones%2C%20professional%20hardscape%20photography&width=800&height=600&seq=gal3&orientation=landscape', material: 'Natural Stone Block' },
  { id: 21, title: 'Outdoor Living Space', category: 'Outdoor Living', large: 'https://readdy.ai/api/search-image?query=complete%20outdoor%20living%20space%20with%20paver%20patio%20fire%20pit%20seating%20wall%20and%20pergola%20in%20Florida%20backyard%2C%20warm%20evening%20light%2C%20luxury%20residential%20hardscape%2C%20Belgard%20pavers%2C%20professional%20landscape%20photography&width=800&height=600&seq=gal4&orientation=landscape', material: 'Belgard Mega Arbel' },
];

const allProjects = [...projects.map(p => ({ ...p, large: p.large })), ...extraImages];
const categories = ['All', 'Driveways', 'Patios', 'Walkways', 'Pool Decks', 'Walls', 'Outdoor Living'];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selected, setSelected] = useState<typeof allProjects[0] | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const filtered = activeCategory === 'All'
    ? allProjects
    : allProjects.filter(p => p.category === activeCategory);

  return (
    <>
      <Navbar />
      <main>
        {/* 1. HERO */}
        <section className="relative min-h-[60vh] flex items-end pb-20 bg-parkside-darker"
          style={{ backgroundImage: "url('https://www.parksidepavers.com/files/projects/16/images/thumb/thumb_large_IMG%5F2840%2Ejpg')", backgroundSize: 'cover', backgroundPosition: 'center top' }}>
          <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker/92 via-parkside-darker/50 to-parkside-darker/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-parkside-darker/60 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="flex flex-wrap gap-2 mb-5">
              {['✔ Real Projects', '✔ Tampa Bay Installations', '✔ ICPI Certified Work'].map((b) => (
                <span key={b} className="bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3.5 py-1.5 rounded-full border border-white/20">{b}</span>
              ))}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[68px] text-white max-w-3xl mb-5 leading-tight tracking-display">
              Our Paver Project Gallery
            </h1>
            <p className="text-white/75 text-lg max-w-xl mb-8 leading-relaxed">
              Browse real installations by Parkside Pavers across Tampa Bay. Driveways, patios, pool decks, outdoor living spaces — all custom built to last.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="bg-parkside-amber text-white font-bold px-8 py-4 rounded-xl hover:bg-parkside-amber/90 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Start Your Project <i className="ri-arrow-right-line" />
              </Link>
              <a href="tel:813-404-6313" className="bg-white/12 backdrop-blur-sm border border-white/30 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/22 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill" /> (813) 404-6313
              </a>
            </div>
          </div>
        </section>

        {/* 2. TRUST BAR */}
        <section className="bg-white border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: 'ri-star-fill', label: '5-Star Rated on Google & Yelp', color: 'text-parkside-amber' },
                { icon: 'ri-image-line', label: '500+ Projects Completed', color: 'text-parkside-green' },
                { icon: 'ri-award-fill', label: 'ICPI Certified Installer', color: 'text-parkside-amber' },
                { icon: 'ri-shield-check-fill', label: 'Licensed, Bonded & Insured', color: 'text-parkside-green' },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-cream rounded-lg flex-shrink-0">
                    <i className={`${t.icon} ${t.color} text-base`} />
                  </div>
                  <span className="font-semibold text-parkside-dark text-sm">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. GALLERY GRID */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-3">Our Work</span>
              <h2 className="font-display text-4xl md:text-5xl text-parkside-dark tracking-display leading-tight mb-4">Real Projects, Real Results</h2>
              <p className="text-parkside-muted max-w-xl mx-auto text-base">Every project custom-designed and ICPI-certified installed throughout Tampa Bay, Florida.</p>
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map((cat) => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat ? 'bg-parkside-green text-white' : 'bg-white border border-stone-200 text-parkside-dark hover:border-parkside-green/40 hover:text-parkside-green'}`}>
                  {cat}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((project) => (
                <div key={project.id} className="group relative rounded-2xl overflow-hidden cursor-pointer border border-stone-100 hover:border-parkside-green/30 transition-all"
                  style={{ height: '280px' }} onClick={() => setSelected(project)}>
                  <div className="w-full h-full absolute inset-0">
                    <img src={project.large} alt={project.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker/85 via-parkside-darker/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                      <i className="ri-zoom-in-line text-white text-xl" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="inline-block bg-parkside-amber/90 text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg mb-2">{project.category}</span>
                    <h3 className="text-white font-bold text-base leading-tight">{project.title}</h3>
                    {'material' in project && <p className="text-white/60 text-xs mt-0.5">{project.material}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {selected && (
          <div className="fixed inset-0 z-50 bg-parkside-darker/95 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setSelected(null)} className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors cursor-pointer">
                <i className="ri-close-line text-xl" />
              </button>
              <div className="rounded-2xl overflow-hidden" style={{ height: '480px' }}>
                <img src={selected.large} alt={selected.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg">{selected.title}</h3>
                  {'material' in selected && <p className="text-white/60 text-sm mt-0.5">{selected.material}</p>}
                </div>
                <Link to="/contact" onClick={() => setSelected(null)} className="bg-parkside-amber text-white font-bold px-6 py-2.5 rounded-xl hover:bg-parkside-amber/90 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2">
                  Get a Similar Project <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* 4. AI ANSWER BLOCK */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-parkside-cream rounded-2xl p-8 md:p-10 border border-stone-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-amber/12 rounded-lg">
                    <i className="ri-robot-line text-parkside-amber text-lg" />
                  </div>
                  <span className="text-parkside-amber text-xs font-bold uppercase tracking-widest">About Our Work</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-parkside-dark mb-5 tracking-display leading-tight">Paver Installation Portfolio — Tampa Bay, FL</h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-4">
                  This gallery showcases real paver installation projects completed by Parkside Pavers throughout Tampa Bay. Every project is custom-designed and installed by our ICPI-certified crew using premium materials from Belgard, Tremron, and other top brands.
                </p>
                <p className="text-parkside-muted text-base leading-relaxed">Projects range from residential driveways and backyard patios to pool decks, retaining walls, and complete outdoor living transformations across Tampa, St. Petersburg, Clearwater, and Tarpon Springs.</p>
              </div>
              <div className="bg-parkside-darker rounded-2xl p-8 md:p-10">
                <h3 className="text-white font-bold text-xl mb-5">Project Types We Complete</h3>
                <ul className="space-y-3.5">
                  {['Custom paver driveways', 'Backyard patio installations', 'Pool deck paver systems', 'Front walkways & entryways', 'Retaining walls & steps', 'Outdoor living & seating areas', 'Full hardscape design & build'].map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm text-white/70">
                      <div className="w-6 h-6 flex items-center justify-center bg-parkside-amber/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-amber text-sm" />
                      </div>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5–6. PROCESS + WHY (mid-page CTA) */}
        <section className="py-10 bg-parkside-dark border-y border-white/8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-parkside-amber/20 rounded-xl flex-shrink-0">
                  <i className="ri-time-fill text-parkside-amber text-lg" />
                </div>
                <div>
                  <p className="text-white font-bold text-base">Like What You See? Let&apos;s Build Yours</p>
                  <p className="text-white/55 text-sm mt-0.5">Free estimate, no commitment — scheduling 2–3 weeks out.</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a href="tel:813-404-6313" className="flex items-center gap-2 border border-white/25 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  <i className="ri-phone-fill" /> (813) 404-6313
                </a>
                <Link to="/contact" className="bg-parkside-amber text-white font-bold px-6 py-2.5 rounded-xl hover:bg-parkside-amber/90 transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-1.5">
                  Get a Free Estimate <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 7. SERVICE AREAS */}
        <ServiceAreas />

        {/* 8. TESTIMONIALS */}
        <Testimonials />

        {/* 9. FAQ */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-2">
                <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">FAQs</span>
                <h2 className="font-display text-4xl text-parkside-dark mb-5 tracking-display leading-tight">Frequently Asked Questions</h2>
                <div className="bg-white rounded-2xl p-5 border border-stone-100">
                  <a href="tel:813-404-6313" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center bg-parkside-green/15 rounded-xl flex-shrink-0">
                      <i className="ri-phone-fill text-parkside-green text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-parkside-stone font-semibold uppercase tracking-wider">Call Direct</p>
                      <p className="text-parkside-dark font-bold group-hover:text-parkside-green transition-colors">(813) 404-6313</p>
                    </div>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-2">
                {homeFaqs.slice(0, 4).map((faq, idx) => (
                  <div key={idx} className={`rounded-2xl border overflow-hidden ${openFaq === idx ? 'border-parkside-green/30 bg-white' : 'border-stone-100 bg-white'}`}>
                    <button className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${openFaq === idx ? 'bg-parkside-green text-white' : 'bg-parkside-cream text-parkside-muted'}`}>
                          <i className="ri-question-line text-xs" />
                        </div>
                        <span className="text-parkside-dark font-semibold text-sm leading-snug">{faq.question}</span>
                      </div>
                      <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-parkside-cream">
                        <i className={`text-parkside-green text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </span>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6">
                        <div className="ml-9 border-l-2 border-parkside-green/30 pl-4">
                          <p className="text-parkside-muted text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 10. FINAL CTA */}
        <section className="py-16 bg-parkside-green">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Your Project Awaits</span>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-4 tracking-display leading-tight">Ready to Build Your Dream Outdoor Space?</h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">Join hundreds of Tampa Bay homeowners who&apos;ve transformed their properties with Parkside Pavers.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="bg-white text-parkside-green font-bold px-8 py-4 rounded-xl hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                Get a Free Estimate <i className="ri-arrow-right-line" />
              </Link>
              <a href="tel:813-404-6313" className="border-2 border-white/40 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-phone-fill" /> Call Now
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
